import type { RootState } from '@/store';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { aguardarExportacaoPronta } from '@/shared/utils/aguardarExportacaoPronta';
import { resolveWorkspaceId } from '@/shared/utils/workspace';

import { getErrorMessage } from '@/shared/utils/error';
import type {
  ClosePhoneConflictResult,
  ValidatePhoneConflictResult,
} from '@/features/negociacaoIndividualWizard/types';

import type {
  ArchiveNegociacaoCasoResult,
  CadenciaBucket,
  CancelNegociacaoStartResult,
  CloseNegociacaoCasoResult,
  DashboardCasesPage,
  EncerrarNegociacaoCasoResult,
  EngagementCategory,
  FunnelStage,
  GeneralDashboard,
  ListNegociacaoCasosResponse,
  NegociacaoCasoStatus,
  NegociacaoPeriodoParams,
  QualitativeDashboard,
  ReabrirNegociacaoCasoResult,
  UnarchiveNegociacaoCasoResult,
  UpdateNegociacaoCasoStatusResult,
} from '../types';
import type {
  BulkEncerramentoInput,
  BulkEncerramentoResult,
  EncerramentoNegociacaoInput,
  ReaberturaNegociacaoInput,
  TipoEncerramento,
} from '@/features/negociacoes';
import type {
  CpfCnpjLookupResult,
  MapeamentoColunas,
  NegociacaoItemEditData,
  NegociacaoItemEditInput,
  PreValidationIssue,
} from '../types/batch.types';
import type {
  ListLotesParams,
  ListLotesResponse,
  LoteDetailError,
  LoteDetailParams,
  LoteDetailResponse,
} from '../types/lotes.types';
import type { CorrecaoNomesLoteResult } from '@/shared/types/correcaoNomes';
import type { AjusteNegociacaoGrupo, ReenviarItemLoteResult } from '../types/verificacao.types';
import type { NegociacaoLoteStatusEvent, NegociacaoLoteTotais } from '../types/loteStatus.types';
import { type NegociacaoLoteItemDTO, negociacoesService } from './services';

export interface FetchNegociacaoCasosInput extends NegociacaoPeriodoParams {
  search?: string;
  type?: 'INDIVIDUAL' | 'BULK';
  status?: NegociacaoCasoStatus;
  statusIn?: NegociacaoCasoStatus[];
  tabCountsStatus?: NegociacaoCasoStatus;
  tabCountsStatusIn?: NegociacaoCasoStatus[];
  cadenceBucketIn?: CadenciaBucket[];
  extrajudicialItemId?: string;
  humanIntervention?: boolean;
  tabCountsHumanIntervention?: boolean;
  archived?: boolean;
  tipoEncerramento?: TipoEncerramento;
  tipoEncerramentoIn?: TipoEncerramento[];
  scenarioTypeIn?: string[];
  situacaoIdIn?: string[];
  pendency?: 'WITH' | 'WITHOUT';
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'client' | 'type' | 'objective' | 'status' | 'temperature' | 'dataAcordo';
  sortOrder?: 'asc' | 'desc';
}

export const fetchNegociacaoCasos = createAsyncThunk<
  ListNegociacaoCasosResponse,
  FetchNegociacaoCasosInput | undefined,
  { rejectValue: string; state: RootState }
>('negociacoes/fetchNegociacaoCasos', async (input, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    const escritorioId = resolveWorkspaceId({ user });

    return await negociacoesService.listCasos({
      empresaId: user.empresaId,
      escritorioId: escritorioId || undefined,
      extrajudicialItemId: input?.extrajudicialItemId,
      search: input?.search,
      type: input?.type,
      status: input?.status,
      statusIn: input?.statusIn,
      tabCountsStatus: input?.tabCountsStatus,
      tabCountsStatusIn: input?.tabCountsStatusIn,
      cadenceBucketIn: input?.cadenceBucketIn,
      humanIntervention: input?.humanIntervention,
      tabCountsHumanIntervention: input?.tabCountsHumanIntervention,
      archived: input?.archived,
      tipoEncerramento: input?.tipoEncerramento,
      tipoEncerramentoIn: input?.tipoEncerramentoIn,
      scenarioTypeIn: input?.scenarioTypeIn,
      situacaoIdIn: input?.situacaoIdIn,
      pendency: input?.pendency,
      periodoCampo: input?.periodoCampo,
      periodoInicio: input?.periodoInicio,
      periodoFim: input?.periodoFim,
      page: input?.page,
      limit: input?.limit,
      sortBy: input?.sortBy,
      sortOrder: input?.sortOrder,
    });
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar negociações');
  }
});

export const fetchNegociacaoPeriodoPreview = createAsyncThunk<
  number,
  FetchNegociacaoCasosInput,
  { rejectValue: string; state: RootState }
>('negociacoes/fetchNegociacaoPeriodoPreview', async (input, { rejectWithValue, getState }) => {
  try {
    const user = getState().auth.user;
    if (!user?.empresaId) return rejectWithValue('Não autenticado');
    const escritorioId = resolveWorkspaceId({ user });
    const response = await negociacoesService.listCasos({
      ...input,
      empresaId: user.empresaId,
      escritorioId: escritorioId || undefined,
      page: 1,
      limit: 1,
    });
    return response.total;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao calcular a prévia do período',
    );
  }
});

export interface NegociacaoSituacaoOption {
  id: string;
  key: string;
  label: string;
  isShared: boolean;
}

export const fetchNegociacaoSituacoes = createAsyncThunk<
  NegociacaoSituacaoOption[],
  void,
  { rejectValue: string; state: RootState }
>('negociacoes/fetchSituacoes', async (_, { rejectWithValue }) => {
  try {
    const response = await negociacoesService.listSituacoes();
    return response.items.map((item) => ({
      id: item.id,
      key: item.key,
      label: item.label,
      isShared: item.isShared,
    }));
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const pauseNegociacaoCaso = createAsyncThunk<
  UpdateNegociacaoCasoStatusResult,
  { caseId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/pauseCaso', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.pauseCaso(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const corrigirNomesLoteNegociacao = createAsyncThunk<
  CorrecaoNomesLoteResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/corrigirNomesLote', async (loteId, { rejectWithValue }) => {
  try {
    return await negociacoesService.corrigirNomesEmLote(loteId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const resumeNegociacaoCaso = createAsyncThunk<
  UpdateNegociacaoCasoStatusResult,
  { caseId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/resumeCaso', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.resumeCaso(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const archiveNegociacaoCaso = createAsyncThunk<
  ArchiveNegociacaoCasoResult,
  { caseId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/archiveCaso', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.archiveCaso(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const unarchiveNegociacaoCaso = createAsyncThunk<
  UnarchiveNegociacaoCasoResult,
  { caseId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/unarchiveCaso', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.unarchiveCaso(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const closeNegociacaoCaso = createAsyncThunk<
  CloseNegociacaoCasoResult,
  { caseId: string; resultStatus: CloseNegociacaoCasoResult['resultStatus'] },
  { rejectValue: string; state: RootState }
>('negociacoes/closeCaso', async ({ caseId, resultStatus }, { rejectWithValue }) => {
  try {
    return await negociacoesService.closeCaso(caseId, resultStatus);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const downloadModeloCsvNegociacao = createAsyncThunk<
  void,
  { flowId: string; agentName: string },
  { rejectValue: string; state: RootState }
>('negociacoes/downloadModeloCsv', async ({ flowId, agentName }, { rejectWithValue }) => {
  try {
    const blob = await negociacoesService.downloadModeloCsv(flowId);
    const url = window.URL.createObjectURL(blob);
    const link = document.body.appendChild(document.createElement('a'));
    link.href = url;
    link.download = `modelo_negociacao_${agentName.toLowerCase().replace(/\s+/g, '_')}.xlsx`;
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao baixar modelo');
  }
});

export interface UploadCsvRejectPayload {
  message: string;
  code?: string;
  issues?: PreValidationIssue[];
  hasCritical?: boolean;
  totalRows?: number;
}

export const uploadCsvNegociacao = createAsyncThunk<
  { loteId: string },
  {
    flowId: string;
    title: string;
    file: File;
    mapeamento?: MapeamentoColunas;
    ignoreRows?: number[];
  },
  { rejectValue: UploadCsvRejectPayload; state: RootState }
>('negociacoes/uploadCsv', async (params, { rejectWithValue }) => {
  try {
    return await negociacoesService.uploadCsv(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao fazer upload';
    const augmented = error as Error & {
      code?: string;
      issues?: PreValidationIssue[];
      hasCritical?: boolean;
      totalRows?: number;
    };
    if (augmented?.code === 'SPREADSHEET_VALIDATION_FAILED') {
      return rejectWithValue({
        message,
        code: augmented.code,
        issues: augmented.issues ?? [],
        hasCritical: augmented.hasCritical ?? true,
        totalRows: augmented.totalRows,
      });
    }
    return rejectWithValue({ message });
  }
});

export const fetchLoteItemsNegociacao = createAsyncThunk<
  { items: NegociacaoLoteItemDTO[]; total: number; totais: NegociacaoLoteTotais },
  { loteId: string; page?: number; pageSize?: number; status?: string },
  { rejectValue: string; state: RootState }
>('negociacoes/fetchLoteItems', async (params, { rejectWithValue }) => {
  try {
    return await negociacoesService.listLoteItems(params.loteId, {
      page: params.page,
      pageSize: params.pageSize,
      status: params.status,
    });
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar itens do lote',
    );
  }
});

export const listarAjustesNegociacao = createAsyncThunk<
  AjusteNegociacaoGrupo[],
  string | undefined,
  { rejectValue: string; state: RootState }
>('negociacoes/listarAjustes', async (loteId, { rejectWithValue }) => {
  try {
    return await negociacoesService.listarAjustesNegociacao(loteId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Erro ao carregar as pendências de ajuste.'));
  }
});

export const reenviarItemLoteNegociacao = createAsyncThunk<
  ReenviarItemLoteResult,
  { loteId: string; itemId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/reenviarItemLote', async (params, { rejectWithValue }) => {
  try {
    return await negociacoesService.reenviarItemLote(params.loteId, params.itemId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Não foi possível reenviar o contato.'));
  }
});

export const ignorarItemLoteNegociacao = createAsyncThunk<
  void,
  { loteId: string; itemId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/ignorarItemLote', async (params, { rejectWithValue }) => {
  try {
    await negociacoesService.ignorarItemLote(params.loteId, params.itemId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Não foi possível ignorar a pendência.'));
  }
});

export const reverterItemLoteNegociacao = createAsyncThunk<
  void,
  { loteId: string; itemId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/reverterItemLote', async (params, { rejectWithValue }) => {
  try {
    await negociacoesService.reverterItemLote(params.loteId, params.itemId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Não foi possível reverter a pendência.'));
  }
});

export const removeLoteItemNegociacao = createAsyncThunk<
  void,
  { loteId: string; itemId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/removeLoteItem', async (params, { rejectWithValue }) => {
  try {
    await negociacoesService.removeLoteItem(params.loteId, params.itemId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao remover item');
  }
});

export const discardAcordosExistentesNegociacao = createAsyncThunk<
  { discarded: number },
  { loteId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/discardAcordosExistentes', async ({ loteId }, { rejectWithValue, dispatch }) => {
  try {
    const result = await negociacoesService.discardAcordosExistentes(loteId);
    await dispatch(fetchLoteItemsNegociacao({ loteId }));
    return result;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao descartar acordos existentes',
    );
  }
});

export const confirmLoteNegociacao = createAsyncThunk<
  { loteId: string },
  { loteId: string; startMode: 'NOW' | 'SCHEDULED'; scheduledFor?: string },
  { rejectValue: string; state: RootState }
>('negociacoes/confirmLote', async (params, { rejectWithValue }) => {
  try {
    const { loteId, ...rest } = params;
    return await negociacoesService.confirmLote(loteId, rest);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao confirmar lote');
  }
});

export const fetchLoteStatus = createAsyncThunk<
  NegociacaoLoteStatusEvent,
  { loteId: string },
  { rejectValue: string; state: RootState }
>('negociacoes/fetchLoteStatus', async ({ loteId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.getLoteStatus(loteId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao consultar status do lote',
    );
  }
});

export const obterItemParaEdicaoNegociacao = createAsyncThunk<
  NegociacaoItemEditData,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/obterItemParaEdicao', async (itemId, { rejectWithValue }) => {
  try {
    return await negociacoesService.obterItemParaEdicao(itemId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar item');
  }
});

export const atualizarItemNegociacao = createAsyncThunk<
  { success: boolean },
  { itemId: string; input: NegociacaoItemEditInput },
  { rejectValue: string; state: RootState }
>('negociacoes/atualizarItem', async ({ itemId, input }, { rejectWithValue }) => {
  try {
    return await negociacoesService.atualizarItem(itemId, input);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao atualizar item');
  }
});

export const consultarCpfCnpj = createAsyncThunk<
  CpfCnpjLookupResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/consultarCpfCnpj', async (documento, { rejectWithValue }) => {
  try {
    return await negociacoesService.validarCpfCnpj(documento);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao consultar CPF/CNPJ');
  }
});

interface ExportNegociacaoCasosCsvInput extends NegociacaoPeriodoParams {
  search?: string;
  type?: 'INDIVIDUAL' | 'BULK';
  status?: NegociacaoCasoStatus;
  statusIn?: NegociacaoCasoStatus[];
  cadenceBucketIn?: CadenciaBucket[];
  extrajudicialItemId?: string;
  loteId?: string;
  humanIntervention?: boolean;
  archived?: boolean;
  tipoEncerramento?: TipoEncerramento;
  tipoEncerramentoIn?: TipoEncerramento[];
  scenarioTypeIn?: string[];
  pendency?: 'WITH' | 'WITHOUT';
  caseIds?: string[];
  situacaoIdIn?: string[];
  format?: 'csv' | 'xlsx';
}

export const validatePhoneConflictLote = createAsyncThunk<
  ValidatePhoneConflictResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/validatePhoneConflictLote', async (phone, { rejectWithValue }) => {
  try {
    return await negociacoesService.validatePhoneConflict(phone);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const getOpenCaseConflictLote = createAsyncThunk<
  ValidatePhoneConflictResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/getOpenCaseConflictLote', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacoesService.getOpenCaseConflict(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const closePhoneConflictLote = createAsyncThunk<
  ClosePhoneConflictResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacoes/closePhoneConflictLote', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacoesService.closePhoneConflict(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const encerrarNegociacaoCaso = createAsyncThunk<
  EncerrarNegociacaoCasoResult,
  EncerramentoNegociacaoInput,
  { rejectValue: string; state: RootState }
>('negociacoes/encerrarCaso', async (input, { rejectWithValue }) => {
  try {
    return await negociacoesService.encerrarCaso(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const reabrirNegociacaoCaso = createAsyncThunk<
  ReabrirNegociacaoCasoResult,
  ReaberturaNegociacaoInput,
  { rejectValue: string; state: RootState }
>('negociacoes/reabrirCaso', async (input, { rejectWithValue }) => {
  try {
    return await negociacoesService.reabrirCaso(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const bulkEncerrarNegociacaoCasos = createAsyncThunk<
  BulkEncerramentoResult,
  BulkEncerramentoInput,
  { rejectValue: string; state: RootState }
>('negociacoes/bulkEncerrar', async (input, { rejectWithValue }) => {
  try {
    return await negociacoesService.bulkEncerrarCasos(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const cancelNegociacaoStartCaso = createAsyncThunk<
  CancelNegociacaoStartResult,
  { caseId: string },
  { rejectValue: string }
>('negociacoes/cancelStartCaso', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacoesService.cancelStart(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export interface ExportNegociacaoCasosCsvResult {
  truncated: boolean;
}

export const exportNegociacaoCasosCsv = createAsyncThunk<
  ExportNegociacaoCasosCsvResult,
  ExportNegociacaoCasosCsvInput | undefined,
  { rejectValue: string; state: RootState }
>('negociacoes/exportNegociacaoCasosCsv', async (input, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    if (typeof window === 'undefined') {
      return rejectWithValue('Exportação disponível apenas no navegador');
    }

    const { exportId } = await negociacoesService.startExportCasos({
      extrajudicialItemId: input?.extrajudicialItemId,
      loteId: input?.loteId,
      search: input?.search,
      type: input?.type,
      status: input?.status,
      statusIn: input?.statusIn,
      cadenceBucketIn: input?.cadenceBucketIn,
      humanIntervention: input?.humanIntervention,
      archived: input?.archived,
      tipoEncerramento: input?.tipoEncerramento,
      tipoEncerramentoIn: input?.tipoEncerramentoIn,
      scenarioTypeIn: input?.scenarioTypeIn,
      pendency: input?.pendency,
      caseIds: input?.caseIds,
      situacaoIdIn: input?.situacaoIdIn,
      periodoCampo: input?.periodoCampo,
      periodoInicio: input?.periodoInicio,
      periodoFim: input?.periodoFim,
      format: input?.format,
    });

    const pronta = await aguardarExportacaoPronta((signal) =>
      negociacoesService.getExportCasosStatus(exportId, signal),
    );
    window.open(pronta.signedUrl, '_blank', 'noopener,noreferrer');
    return { truncated: pronta.truncated };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao exportar CSV');
  }
});

export const getGeneralDashboard = createAsyncThunk<
  GeneralDashboard,
  void,
  { rejectValue: string }
>('extrajudicial/dashboard/negotiations/general', async (_, { rejectWithValue }) => {
  try {
    return await negociacoesService.getGeneralDashboard();
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao obter dashboard geral',
    );
  }
});

export const getQualitativeDashboard = createAsyncThunk<
  QualitativeDashboard,
  { force?: boolean } | void,
  { rejectValue: string }
>('extrajudicial/dashboard/negotiations/qualitative', async (arg, { rejectWithValue }) => {
  try {
    const force = arg && 'force' in arg ? arg.force : false;
    return await negociacoesService.getQualitativeDashboard(force);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao obter dashboard qualitativo',
    );
  }
});

export const getFunnelCases = createAsyncThunk<
  DashboardCasesPage,
  { stage: FunnelStage; page: number; limit: number },
  { rejectValue: string }
>('extrajudicial/dashboard/negotiations/funnelCases', async (arg, { rejectWithValue }) => {
  try {
    return await negociacoesService.getFunnelCases(arg.stage, arg.page, arg.limit);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao listar casos da etapa do funil',
    );
  }
});

export const getEngagementCases = createAsyncThunk<
  DashboardCasesPage,
  { categoria: EngagementCategory; page: number; limit: number },
  { rejectValue: string }
>('extrajudicial/dashboard/negotiations/engagementCases', async (arg, { rejectWithValue }) => {
  try {
    return await negociacoesService.getEngagementCases(arg.categoria, arg.page, arg.limit);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao listar casos por perfil de engajamento',
    );
  }
});

export const listarLotesNegociacao = createAsyncThunk<
  ListLotesResponse,
  ListLotesParams | undefined,
  { rejectValue: string; state: RootState }
>('negociacoes/lotes/listar', async (params, { rejectWithValue }) => {
  try {
    return await negociacoesService.listLotes(params ?? {});
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao listar lotes de negociação',
    );
  }
});

export const fetchLoteDetail = createAsyncThunk<
  LoteDetailResponse,
  { loteId: string } & LoteDetailParams,
  { rejectValue: LoteDetailError; state: RootState }
>('negociacoes/lotes/detail', async ({ loteId, ...params }, { rejectWithValue }) => {
  try {
    return await negociacoesService.getLoteDetail(loteId, params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar detalhes do lote';
    const statusCode = (error as { statusCode?: number })?.statusCode;
    return rejectWithValue({ message, statusCode });
  }
});
